// src/lib/usageQuotas.js
//
// LinkedIn-style usage quota system. Users experience full features
// first, hit soft gates after N uses per month.
//
// Anonymous: tracked via localStorage (IP-based server-side for search)
// Registered: tracked via Clerk publicMetadata (existing usage-tracking.js)
//
// All quota checks are client-side for instant UX. Server-side tracking
// (for search) uses the existing checkIpSearchLimit pattern.

// ── Tier quotas ──────────────────────────────────────────────────────────
export const USAGE_QUOTAS = {
  anonymous: {
    dealViews: 5,
    counterpartViews: 10,
    ecosystemSearches: 3,
    matchupComparisons: 2,
    feedDealsPerSector: 10,
    searches: 3, // server enforces 3/day per IP via api/_lib/rateLimit.js
  },
  free: {
    dealViews: 20,
    counterpartViews: 40,
    ecosystemSearches: 10,
    matchupComparisons: 5,
    feedDealsPerSector: 30,
    searches: 3, // server enforces 3/day per IP for non-Pro free users
  },
  basic: {
    dealViews: 100,
    counterpartViews: 200,
    ecosystemSearches: 50,
    matchupComparisons: 25,
    feedDealsPerSector: 100,
    searches: 10, // server enforces 10/day per IP for basic users
  },
  pro: {
    dealViews: Infinity,
    counterpartViews: Infinity,
    ecosystemSearches: Infinity,
    matchupComparisons: Infinity,
    feedDealsPerSector: Infinity,
    searches: Infinity,
  },
  admin: {
    dealViews: Infinity,
    counterpartViews: Infinity,
    ecosystemSearches: Infinity,
    matchupComparisons: Infinity,
    feedDealsPerSector: Infinity,
    searches: Infinity,
  },
};

// Alias enterprise to pro
USAGE_QUOTAS.enterprise = USAGE_QUOTAS.pro;

// ── Monthly period key ───────────────────────────────────────────────────
function getPeriodKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ── localStorage-based tracking (anonymous + registered client-side) ─────
const STORAGE_KEY = 'gis_usage';

function loadUsage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    const period = getPeriodKey();
    // Auto-reset on month change
    if (data._period !== period) return { _period: period };
    return data;
  } catch {
    return {};
  }
}

function saveUsage(data) {
  try {
    data._period = getPeriodKey();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/**
 * Get current usage count for a namespace.
 */
export function getUsage(namespace) {
  const data = loadUsage();
  return data[namespace] || 0;
}

/**
 * Increment usage count. Returns the new count.
 */
export function incrementUsage(namespace) {
  const data = loadUsage();
  data[namespace] = (data[namespace] || 0) + 1;
  saveUsage(data);
  return data[namespace];
}

/**
 * Check if the user can perform an action.
 * Returns { allowed, used, limit, remaining }
 */
export function checkQuota(namespace, userTier = 'anonymous') {
  const tier = userTier || 'anonymous';
  const quotas = USAGE_QUOTAS[tier] || USAGE_QUOTAS.anonymous;
  const limit = quotas[namespace] ?? 0;
  const used = getUsage(namespace);

  if (limit === Infinity) {
    return { allowed: true, used, limit, remaining: Infinity };
  }

  return {
    allowed: used < limit,
    used,
    limit,
    remaining: Math.max(0, limit - used),
  };
}

/**
 * Use an action — increments and returns the check result.
 * Call this BEFORE showing content. If not allowed, show the gate instead.
 */
export function consumeQuota(namespace, userTier = 'anonymous') {
  const check = checkQuota(namespace, userTier);
  if (!check.allowed) return check;

  const newCount = incrementUsage(namespace);
  return {
    allowed: true,
    used: newCount,
    limit: check.limit,
    remaining: Math.max(0, check.limit - newCount),
  };
}

/**
 * Get usage summary for display ("3 of 5 free views remaining").
 */
export function getUsageSummary(namespace, userTier = 'anonymous') {
  const { used, limit, remaining } = checkQuota(namespace, userTier);
  if (limit === Infinity) return null; // Pro/admin — no message needed
  return { used, limit, remaining };
}

/**
 * Get the upgrade CTA text based on tier.
 */
export function getUpgradeText(userTier) {
  switch (userTier) {
    case 'anonymous':
      return { action: 'Register free', benefit: 'Get 4x more views and email alerts' };
    case 'free':
      return { action: 'Upgrade to Basic', benefit: 'Get 5x more views and full access' };
    case 'basic':
      return { action: 'Upgrade to Pro', benefit: 'Unlimited access to all features' };
    default:
      return null;
  }
}
